/**
 * V250: 증명서 관리 페이지 (HR용)
 *
 * 탭 1: 요청 관리 — 직원 증명서 요청 목록, 승인/반려
 * 탭 2: 양식 관리 — 증명서 템플릿 CRUD
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Check, X, Download, Loader, AlertCircle, Search,
  Plus, Settings, ClipboardList, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useModalOverlayClose } from '../../shared/hooks/useModalOverlayClose';
import { useCertificates, CertificateRequest, CertificateTemplate } from '../../shared/hooks/useCertificates';
import { useAuthorization } from '../../shared/hooks/useAuthorization';

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: '대기중', color: '#d97706', bg: '#fef3c7' },
  approved: { label: '승인', color: '#059669', bg: '#d1fae5' },
  rejected: { label: '반려', color: '#dc2626', bg: '#fee2e2' },
  pdf_failed: { label: 'PDF 오류', color: '#dc2626', bg: '#fee2e2' },
};

const TYPE_LABELS: Record<string, string> = {
  employment: '재직증명서',
  career: '경력증명서',
  salary: '급여증명서',
};

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'pending', label: '대기중' },
  { value: 'approved', label: '승인' },
  { value: 'rejected', label: '반려' },
  { value: 'pdf_failed', label: 'PDF 오류' },
];

// V57: 페이지네이션 상수
const PAGE_SIZE = 15;

export function HrCertificateManagementPage() {
  const {
    isLoading,
    error,
    listRequestsForHr,
    approveRequest,
    rejectRequest,
    getDownloadUrl,
    listTemplates,
    createTemplate,
    updateTemplate,
  } = useCertificates();

  // ---- 권한 (system_admin 회사 선택) ----
  const { user, hasAnyRole, hasPermission } = useAuthorization();
  const isGlobalScope = useMemo(() => hasAnyRole(['system_admin', 'role_global']), [hasAnyRole]);
  // V61: role_exec 조회 전용 — 승인/반려/양식 관리 액션 비활성화
  // V62: auth_roles DB에 certificate.manage 미반영 시 역할 기반 폴백 추가
  const canWrite = hasPermission('certificate.manage')
    || hasAnyRole(['role_hr', 'system_admin', 'role_admin_kr', 'role_admin_cn']);
  const [filterCompanyCode, setFilterCompanyCode] = useState<string>('');

  // ---- 탭 ----
  const [activeTab, setActiveTab] = useState<'requests' | 'templates'>('requests');

  // ---- 요청 관리 ----
  const [requests, setRequests] = useState<CertificateRequest[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);

  // V57: 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // ---- 모달 ----
  const [rejectModal, setRejectModal] = useState<CertificateRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  // V273: 모달 오버레이 드래그 오작동 방지
  const rejectOverlayProps = useModalOverlayClose(() => setRejectModal(null));
  const templateFormOverlayProps = useModalOverlayClose(() => setShowTemplateForm(false));

  // ---- 템플릿 관리 ----
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    type: 'employment',
    title: '',
    htmlTemplate: '',
    sealImagePath: '',
    signatureImagePath: '',
  });

  // V57: 페이지네이션 — 코어 fetch (명시적 cursor/page 파라미터)
  const doFetch = useCallback(async (opts?: { cursor?: string; searchPage?: number }) => {
    try {
      setPageError(null);
      const isSearchMode = !!searchQuery?.trim();
      const result = await listRequestsForHr({
        companyCode: filterCompanyCode || undefined,
        status: filterStatus || undefined,
        year: filterYear,
        query: searchQuery || undefined,
        pageSize: PAGE_SIZE,
        ...(isSearchMode ? { page: opts?.searchPage || 1 } : { lastDocId: opts?.cursor }),
      });
      setRequests(result.items);
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
    } catch {
      setPageError('요청 목록을 불러오지 못했습니다.');
    }
  }, [listRequestsForHr, filterCompanyCode, filterStatus, filterYear, searchQuery]);

  const fetchFirstPage = useCallback(() => {
    setCurrentPage(1);
    setPageCursors([undefined]);
    setNextCursor(undefined);
    setHasMore(false);
    doFetch({});
  }, [doFetch]);

  // V57: 페이지네이션 핸들러
  const handleNextPage = useCallback(() => {
    if (!hasMore) return;
    const isSearchMode = !!searchQuery?.trim();
    const newPage = currentPage + 1;
    if (!isSearchMode && nextCursor) {
      setPageCursors(prev => {
        const arr = [...prev];
        arr[currentPage] = nextCursor;
        return arr;
      });
    }
    setCurrentPage(newPage);
    doFetch(isSearchMode ? { searchPage: newPage } : { cursor: nextCursor });
  }, [hasMore, searchQuery, currentPage, nextCursor, doFetch]);

  const handlePrevPage = useCallback(() => {
    if (currentPage <= 1) return;
    const isSearchMode = !!searchQuery?.trim();
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    doFetch(isSearchMode ? { searchPage: newPage } : { cursor: pageCursors[newPage - 1] });
  }, [currentPage, searchQuery, pageCursors, doFetch]);

  const fetchTemplates = useCallback(async () => {
    try {
      setPageError(null);
      const items = await listTemplates({
        companyCode: filterCompanyCode || undefined,
      });
      setTemplates(items);
    } catch {
      setPageError('템플릿 목록을 불러오지 못했습니다.');
    }
  }, [listTemplates, filterCompanyCode]);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchFirstPage();
    } else {
      fetchTemplates();
    }
  }, [activeTab, fetchFirstPage, fetchTemplates]);

  const handleApprove = async (req: CertificateRequest) => {
    if (!window.confirm(`${req.requesterName}님의 ${TYPE_LABELS[req.type] || req.type} 요청을 승인하시겠습니까?`)) return;
    try {
      setProcessing(req.id);
      await approveRequest(req.id);
      // V57: 현재 페이지 재조회
      const isSearchMode = !!searchQuery?.trim();
      await doFetch(isSearchMode ? { searchPage: currentPage } : { cursor: pageCursors[currentPage - 1] });
    } catch {
      // error set by hook
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    try {
      setProcessing(rejectModal.id);
      await rejectRequest(rejectModal.id, rejectReason.trim());
      setRejectModal(null);
      setRejectReason('');
      // V57: 현재 페이지 재조회
      const isSearchMode = !!searchQuery?.trim();
      await doFetch(isSearchMode ? { searchPage: currentPage } : { cursor: pageCursors[currentPage - 1] });
    } catch {
      // error set by hook
    } finally {
      setProcessing(null);
    }
  };

  const handleDownload = async (req: CertificateRequest) => {
    try {
      setDownloading(req.id);
      const result = await getDownloadUrl(req.id);
      const a = document.createElement('a');
      a.href = result.downloadUrl;
      a.target = '_blank';
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // error set by hook
    } finally {
      setDownloading(null);
    }
  };

  const handleCreateTemplate = async () => {
    if (isGlobalScope && !filterCompanyCode) {
      setPageError('회사를 선택한 후 양식을 추가해 주세요.');
      return;
    }
    if (!templateForm.title.trim() || !templateForm.htmlTemplate.trim() || !templateForm.sealImagePath.trim()) return;
    try {
      setProcessing('template-create');
      await createTemplate({
        companyCode: filterCompanyCode || undefined,
        type: templateForm.type,
        title: templateForm.title.trim(),
        htmlTemplate: templateForm.htmlTemplate.trim(),
        sealImagePath: templateForm.sealImagePath.trim(),
        signatureImagePath: templateForm.signatureImagePath.trim() || undefined,
      });
      setShowTemplateForm(false);
      setTemplateForm({ type: 'employment', title: '', htmlTemplate: '', sealImagePath: '', signatureImagePath: '' });
      await fetchTemplates();
    } catch {
      // error set by hook
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleTemplateActive = async (tpl: CertificateTemplate) => {
    const nextActive = !tpl.isActive;
    if (nextActive && !window.confirm(`"${tpl.title}" v${tpl.version}을 활성화하시겠습니까? (기존 활성 양식은 자동 비활성화됩니다)`)) return;
    try {
      setProcessing(tpl.id);
      await updateTemplate({ templateId: tpl.id, isActive: nextActive });
      await fetchTemplates();
    } catch {
      // error set by hook
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return '-';
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const tabStyle = (tab: string) => ({
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: activeTab === tab ? 700 : 500,
    color: activeTab === tab ? '#3b82f6' : '#6b7280',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
    cursor: 'pointer',
  });

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* 헤더 */}
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, marginBottom: 24 }}>증명서 관리</h1>

      {/* 탭 */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        <button onClick={() => setActiveTab('requests')} style={tabStyle('requests')}>
          <ClipboardList size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          요청 관리
        </button>
        <button onClick={() => setActiveTab('templates')} style={tabStyle('templates')}>
          <Settings size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          양식 관리
        </button>
      </div>

      {/* 회사 선택 (system_admin / role_global) */}
      {isGlobalScope && (
        <div style={{ marginBottom: 16 }}>
          <select
            value={filterCompanyCode}
            onChange={e => setFilterCompanyCode(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
          >
            <option value="">전체 회사</option>
            <option value="KR">한국 (KR)</option>
            <option value="CN">중국 (CN)</option>
          </select>
        </div>
      )}

      {/* 에러 */}
      {(pageError || error) && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, backgroundColor: '#fee2e2',
          borderRadius: 8, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} /> {pageError || error}
        </div>
      )}

      {/* ===== 요청 관리 탭 ===== */}
      {activeTab === 'requests' && (
        <>
          {/* 필터 */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={filterYear}
              onChange={e => setFilterYear(Number(e.target.value))}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="이름으로 검색..."
                style={{
                  width: '100%', padding: '8px 12px 8px 32px', borderRadius: 6,
                  border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
                }}
              />
            </div>
            <button
              onClick={fetchFirstPage}
              style={{
                padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer',
              }}
            >
              조회
            </button>
          </div>

          {/* 요청 테이블 */}
          <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>신청자</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>종류</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>용도</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>신청일</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>상태</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>발급번호</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>액션</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      <Loader size={20} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} /> 로딩 중...
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      요청이 없습니다.
                    </td>
                  </tr>
                ) : (
                  requests.map(req => {
                    const status = STATUS_LABELS[req.status] || { label: req.status, color: '#6b7280', bg: '#f3f4f6' };
                    const isProcessing = processing === req.id;
                    return (
                      <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{req.requesterName}</td>
                        <td style={{ padding: '12px 16px' }}>{TYPE_LABELS[req.type] || req.type}</td>
                        <td style={{ padding: '12px 16px' }}>{req.purpose}</td>
                        <td style={{ padding: '12px 16px' }}>{formatDate(req.requestedAt)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                            color: status.color, backgroundColor: status.bg,
                          }}>
                            {status.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                          {req.issueNumber || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            {req.status === 'pending' && canWrite && (
                              <>
                                <button
                                  onClick={() => handleApprove(req)}
                                  disabled={isProcessing}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 2,
                                    padding: '4px 10px', backgroundColor: '#059669', color: '#fff',
                                    border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                                  }}
                                >
                                  {isProcessing ? <Loader size={12} /> : <Check size={12} />} 승인
                                </button>
                                <button
                                  onClick={() => { setRejectModal(req); setRejectReason(''); }}
                                  disabled={isProcessing}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 2,
                                    padding: '4px 10px', backgroundColor: '#dc2626', color: '#fff',
                                    border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                                  }}
                                >
                                  <X size={12} /> 반려
                                </button>
                              </>
                            )}
                            {req.status === 'approved' && (
                              <button
                                onClick={() => handleDownload(req)}
                                disabled={downloading === req.id}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 2,
                                  padding: '4px 10px', backgroundColor: '#3b82f6', color: '#fff',
                                  border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                                }}
                              >
                                {downloading === req.id ? <Loader size={12} /> : <Download size={12} />} 다운로드
                              </button>
                            )}
                            {req.status === 'rejected' && (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>반려 완료</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* V57: 페이지네이션 */}
          {requests.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16,
              padding: '16px 0', marginTop: 8,
            }}>
              <button
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 14px', backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#fff',
                  color: currentPage <= 1 ? '#d1d5db' : '#374151',
                  border: `1px solid ${currentPage <= 1 ? '#e5e7eb' : '#d1d5db'}`,
                  borderRadius: 6, fontSize: 13, cursor: currentPage <= 1 ? 'default' : 'pointer',
                }}
              >
                <ChevronLeft size={14} /> 이전
              </button>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                {currentPage} 페이지
              </span>
              <button
                onClick={handleNextPage}
                disabled={!hasMore}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 14px', backgroundColor: !hasMore ? '#f3f4f6' : '#fff',
                  color: !hasMore ? '#d1d5db' : '#374151',
                  border: `1px solid ${!hasMore ? '#e5e7eb' : '#d1d5db'}`,
                  borderRadius: 6, fontSize: 13, cursor: !hasMore ? 'default' : 'pointer',
                }}
              >
                다음 <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ===== 양식 관리 탭 ===== */}
      {activeTab === 'templates' && (
        <>
          {canWrite && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                onClick={() => setShowTemplateForm(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Plus size={16} /> 양식 추가
              </button>
            </div>
          )}

          <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>종류</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>제목</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>버전</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>활성</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>수정일</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>액션</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && templates.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      <Loader size={20} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} /> 로딩 중...
                    </td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                      등록된 양식이 없습니다.
                    </td>
                  </tr>
                ) : (
                  templates.map(tpl => (
                    <tr key={tpl.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px' }}>{TYPE_LABELS[tpl.type] || tpl.type}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 500 }}>{tpl.title}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>v{tpl.version}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                          backgroundColor: tpl.isActive ? '#059669' : '#d1d5db',
                        }} />
                      </td>
                      <td style={{ padding: '12px 16px' }}>{formatDate(tpl.updatedAt)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggleTemplateActive(tpl)}
                          disabled={!canWrite || processing === tpl.id}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: tpl.isActive ? '#f3f4f6' : '#d1fae5',
                            color: tpl.isActive ? '#6b7280' : '#059669',
                            border: `1px solid ${tpl.isActive ? '#d1d5db' : '#a7f3d0'}`,
                            borderRadius: 4, fontSize: 12, cursor: 'pointer',
                          }}
                        >
                          {processing === tpl.id ? '...' : tpl.isActive ? '비활성화' : '활성화'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 반려 모달 */}
      {rejectModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} {...rejectOverlayProps}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 24, width: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>증명서 반려</h2>
              <button onClick={() => setRejectModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>
              {rejectModal.requesterName}님의 {TYPE_LABELS[rejectModal.type] || rejectModal.type} 요청
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                반려 사유 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="반려 사유를 입력해 주세요."
                rows={3}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                  fontSize: 14, resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>
            {error && (
              <div style={{
                padding: '8px 12px', marginBottom: 16, backgroundColor: '#fee2e2',
                borderRadius: 6, color: '#dc2626', fontSize: 13,
              }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRejectModal(null)}
                style={{
                  padding: '8px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason.trim() || processing === rejectModal.id}
                style={{
                  padding: '8px 20px',
                  backgroundColor: !rejectReason.trim() ? '#fca5a5' : '#dc2626',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
                  cursor: !rejectReason.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {processing === rejectModal.id ? '처리 중...' : '반려'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 양식 생성 모달 */}
      {showTemplateForm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} {...templateFormOverlayProps}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 24, width: 520, maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>양식 추가</h2>
              <button onClick={() => setShowTemplateForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            {isGlobalScope && !filterCompanyCode && (
              <div style={{
                padding: '10px 14px', marginBottom: 16, backgroundColor: '#fef3c7',
                borderRadius: 6, color: '#92400e', fontSize: 13,
              }}>
                회사를 먼저 선택해 주세요. (상단 회사 드롭다운)
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>종류</label>
              <select
                value={templateForm.type}
                onChange={e => setTemplateForm(f => ({ ...f, type: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                <option value="employment">재직증명서</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                제목 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={templateForm.title}
                onChange={e => setTemplateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="예: 재직증명서 기본"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                HTML 템플릿 (placeholder 포함) <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={templateForm.htmlTemplate}
                onChange={e => setTemplateForm(f => ({ ...f, htmlTemplate: e.target.value }))}
                placeholder="위 사실을 증명합니다. ({{employeeName}}, {{department}} 등 placeholder 사용 가능)"
                rows={6}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                사용 가능한 placeholder: {'{{employeeName}}'}, {'{{employeeId}}'}, {'{{department}}'}, {'{{positionTitle}}'}, {'{{jobTitle}}'}, {'{{hireDate}}'}, {'{{employmentType}}'}, {'{{purpose}}'}, {'{{issueDate}}'}
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                직인 이미지 경로 (Storage) <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={templateForm.sealImagePath}
                onChange={e => setTemplateForm(f => ({ ...f, sealImagePath: e.target.value }))}
                placeholder="certificate-assets/KR/seal.png"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>서명 이미지 경로 (선택)</label>
              <input
                type="text"
                value={templateForm.signatureImagePath}
                onChange={e => setTemplateForm(f => ({ ...f, signatureImagePath: e.target.value }))}
                placeholder="certificate-assets/KR/signature.png"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            {error && (
              <div style={{
                padding: '8px 12px', marginBottom: 16, backgroundColor: '#fee2e2',
                borderRadius: 6, color: '#dc2626', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTemplateForm(false)}
                style={{
                  padding: '8px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={processing === 'template-create' || !templateForm.title.trim() || !templateForm.htmlTemplate.trim() || !templateForm.sealImagePath.trim() || (isGlobalScope && !filterCompanyCode)}
                style={{
                  padding: '8px 20px', backgroundColor: (isGlobalScope && !filterCompanyCode) ? '#93c5fd' : '#3b82f6', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
                  cursor: (isGlobalScope && !filterCompanyCode) ? 'not-allowed' : 'pointer',
                }}
              >
                {processing === 'template-create' ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
